import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { useAuth } from '@/shared/components/auth/AuthProvider';
import { userName } from '@/shared/utils/userName';
import { processUsersList } from '../api/users.api';
import { useBootstrapData } from './bootstrap-data.hook';
import type { ICockpitBootstrapData, IUser } from '@condo/domain';
import type { IContactUsersObject, IUsersObject } from '../api/users.api';

// Extract the return type and parameters from the userName function
type UserNameReturnType = ReturnType<typeof userName>;

export type { IContactUsersObject, IUsersObject } from '../api/users.api';

interface UsersContextType {
    users: IUsersObject;
    usersByContactId: IContactUsersObject;
    isLoading: boolean;
    error: Error | null;
    getUser: (uidOrContactId: string | number | undefined) => IUser | null;
    getUserName: (
        uidOrContactId: string | number | undefined,
        fallbackName?: string,
        withEmail?: boolean,
        withLastName?: boolean,
        short?: boolean,
    ) => UserNameReturnType;
    refetchUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

const selectUsers = (bootstrap: ICockpitBootstrapData) => processUsersList(bootstrap.users);
const EMPTY_USERS: IUsersObject = {};
const EMPTY_USERS_BY_CONTACT: IContactUsersObject = {};

interface UsersProviderProps {
    children: ReactNode;
}

export function UsersProvider({ children }: UsersProviderProps) {
    const { user, loading: authLoading } = useAuth();
    const { data, isLoading: queryLoading, error, refetch } = useBootstrapData(selectUsers);
    const users = data?.usersObject ?? EMPTY_USERS;
    const usersByContactId = data?.usersByContact ?? EMPTY_USERS_BY_CONTACT;
    const isLoading = authLoading || queryLoading;

    // Lookup function similar to Vue store getters
    const getUser = useCallback(
        (uidOrContactId: string | number | undefined): IUser | null => {
            if (!uidOrContactId) {
                return null;
            }

            // Try by uid first
            const userByUid = users[uidOrContactId];
            if (userByUid) {
                return userByUid;
            }

            // Try by contactId
            const userByContactId = usersByContactId[Number(uidOrContactId)];
            if (userByContactId) {
                return userByContactId;
            }

            return null;
        },
        [users, usersByContactId],
    );

    // Format user name using the proper userName function (ported from Vue project)
    const getUserName = useCallback(
        (
            uidOrContactId: string | number | undefined,
            fallbackName?: string,
            withEmail?: boolean,
            withLastName?: boolean,
            short?: boolean,
        ): UserNameReturnType => {
            return userName(uidOrContactId, getUser, withEmail, withLastName, short, fallbackName);
        },
        [getUser],
    );

    const refetchUsers = useCallback(async () => {
        if (user && !authLoading) {
            await refetch();
        }
    }, [refetch, user, authLoading]);

    const value = useMemo<UsersContextType>(
        () => ({
            users,
            usersByContactId,
            isLoading,
            error,
            getUser,
            getUserName,
            refetchUsers,
        }),
        [users, usersByContactId, isLoading, error, getUser, getUserName, refetchUsers],
    );

    return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

// Hook to use users context
export function useUsers() {
    const context = useContext(UsersContext);
    if (context === undefined) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
}

// Hook to get a specific user (convenience)
export function useUser(uidOrContactId: string | number | undefined) {
    const { getUser, isLoading } = useUsers();
    return {
        user: getUser(uidOrContactId),
        isLoading,
    };
}
